import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, Loader2, Mail, Phone, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
import { CONTACT_EMAIL, CONTACT_PHONE, CONTACT_PHONE_HREF } from "@/lib/site";

const FIELDS = [
  { name: "name", label: "Your name", type: "text", placeholder: "Jane Founder" },
  { name: "email", label: "Email", type: "email", placeholder: "jane@company.com" },
  { name: "company", label: "Company (optional)", type: "text", placeholder: "Company name" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => {
    if (k === "email") setEmailError("");
    setForm((f) => ({ ...f, [k]: e.target.value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      if (err.response?.status === 422) {
        setEmailError("Please enter a valid email address.");
        toast.error("Please check your details and try again.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="py-24 md:py-32 border-t border-[var(--mpg-border)]"
      data-testid="contact"
    >
      <div className="max-w-[1800px] mx-auto px-8 md:px-16 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="md:col-span-4"
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[var(--mpg-gold)] mb-6">
            Contact
          </p>
          <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight mb-6">
            Let's <span className="italic font-light">talk.</span>
          </h2>
          <p className="text-base font-light leading-relaxed text-[var(--mpg-text-soft)] max-w-xs">
            Partnerships, acquisitions, or general enquiries — reach the group directly.
          </p>

          <div className="mt-10 flex flex-col gap-4">
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="group inline-flex items-center gap-3 text-[var(--mpg-text-soft)] hover:text-[var(--mpg-text)] transition-colors duration-300"
              data-testid="contact-email-link"
            >
              <Mail size={15} className="text-[var(--mpg-gold)]" />
              <span className="font-mono text-xs tracking-[0.12em] link-underline">{CONTACT_EMAIL}</span>
            </a>
            <a
              href={CONTACT_PHONE_HREF}
              className="group inline-flex items-center gap-3 text-[var(--mpg-text-soft)] hover:text-[var(--mpg-text)] transition-colors duration-300"
              data-testid="contact-phone-link"
            >
              <Phone size={15} className="text-[var(--mpg-gold)]" />
              <span className="font-mono text-xs tracking-[0.12em] link-underline">{CONTACT_PHONE}</span>
            </a>
          </div>
        </motion.div>

        {submitted ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="md:col-span-8 flex flex-col justify-center border border-[var(--mpg-border)] px-8 py-14 md:px-14"
            data-testid="contact-success"
          >
            <CheckCircle2 size={28} className="text-[var(--mpg-gold)] mb-6" />
            <h3 className="font-serif text-3xl md:text-4xl leading-tight mb-4">
              Message <span className="italic font-light">received.</span>
            </h3>
            <p className="text-base font-light leading-relaxed text-[var(--mpg-text-soft)] max-w-lg">
              Thank you for reaching out to Madison Price Group. Someone from our team will
              reach out within 7–10 business days.
            </p>
            <button
              type="button"
              onClick={() => setSubmitted(false)}
              className="mt-8 self-start inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-[var(--mpg-text)] group"
              data-testid="contact-send-another"
            >
              <span className="link-underline">Send another message</span>
              <ArrowRight size={16} className="group-hover:translate-x-1.5 transition-transform duration-300" />
            </button>
          </motion.div>
        ) : (
        <motion.form
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.1 }}
          onSubmit={submit}
          className="md:col-span-8 flex flex-col gap-8"
          data-testid="contact-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {FIELDS.map((f) => (
              <div key={f.name} className="group">
                <label className="block font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--mpg-text-soft)] mb-3">
                  {f.label}
                </label>
                <input
                  type={f.type}
                  value={form[f.name]}
                  onChange={update(f.name)}
                  placeholder={f.placeholder}
                  data-testid={`contact-${f.name}`}
                  className={`w-full bg-transparent border-b py-3 text-[var(--mpg-text)] placeholder:text-[rgba(244,240,230,0.25)] focus:outline-none focus:border-[var(--mpg-gold)] transition-colors duration-300 font-light ${
                    f.name === "email" && emailError ? "border-red-400" : "border-[var(--mpg-border)]"
                  }`}
                />
                {f.name === "email" && emailError && (
                  <p className="mt-2 font-mono text-[11px] tracking-wide text-red-400" data-testid="contact-email-error">
                    {emailError}
                  </p>
                )}
              </div>
            ))}
          </div>

          <div>
            <label className="block font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--mpg-text-soft)] mb-3">
              Message
            </label>
            <textarea
              rows={3}
              value={form.message}
              onChange={update("message")}
              placeholder="Tell us a little about what you're building."
              data-testid="contact-message"
              className="w-full bg-transparent border-b border-[var(--mpg-border)] py-3 text-[var(--mpg-text)] placeholder:text-[rgba(244,240,230,0.25)] focus:outline-none focus:border-[var(--mpg-gold)] transition-colors duration-300 font-light resize-none"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="contact-submit"
            className="self-start inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-[var(--mpg-text)] group disabled:opacity-50"
          >
            <span className="link-underline">{submitting ? "Sending" : "Send message"}</span>
            {submitting ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <ArrowRight size={16} className="group-hover:translate-x-1.5 transition-transform duration-300" />
            )}
          </button>
        </motion.form>
        )}
      </div>
    </section>
  );
}
